import type { BoxProps } from '@chakra-ui/react';
import { Flex } from '@chakra-ui/react';
import { Divider, ProductLineItem } from '@time-line/components/common';
import { useProductBundle } from 'hooks/product/useProductBundle';
import { useSanityProduct } from 'hooks/product/useSanityProduct';
import { useLocalStorage, UserLocale } from 'hooks/userPreferences';
import type { ProductBundle } from 'types/cms/blocks/ProductBundle';
import { isSanityImage } from 'types/cms/pages/ProductPage';

import { SHOP_NOW_CTA_LABEL } from './constants';
import { CtaButton } from './CtaButton';
import { StickyCta } from './StickyCta';

interface ProductBundleCtasProps extends BoxProps {
  productBundle: ProductBundle;
  colorScheme: 'white' | 'gray';
}

export function ProductBundleCtas({
  productBundle,
  colorScheme,
  ...props
}: ProductBundleCtasProps) {
  const {
    value: { countryCode, currencyCode },
  } = useLocalStorage(UserLocale);
  const { products, promoCodes } = productBundle;
  const { data: dummyCart } = useProductBundle({
    _key: productBundle._key,
    products,
    promoCodes,
  });

  const sanityProduct = useSanityProduct(products[0].product.slug);

  if (!dummyCart || !sanityProduct) return null;

  const { title, media } = productBundle;
  const totalPrice = dummyCart.totalPrice.amount;
  const hasDiscount = totalPrice < dummyCart.subTotal.amount;
  const compareAtPrice = hasDiscount ? dummyCart.subTotal.amount : undefined;
  const anchorId = productBundle._key;

  const availableForSale = dummyCart.lines.every(
    (item) => item.merchandise.availableForSale,
  );

  return (
    <Flex
      className="hero_product-bundle-ctas"
      direction="column"
      w="full"
      {...props}
    >
      <Divider
        w="full"
        color={colorScheme === 'white' ? 'whiteAlpha.600' : 'blackAlpha.600'}
      />
      <ProductLineItem
        availableForSale={availableForSale}
        colorScheme={colorScheme}
        title={title}
        image={media?.find(isSanityImage)}
        price={totalPrice}
        countryCode={countryCode}
        currencyCode={currencyCode}
        compareAtPrice={compareAtPrice}
      />
      <CtaButton
        size="md"
        colorScheme={colorScheme}
        anchorId={anchorId}
        disabled={!availableForSale}
      >
        {SHOP_NOW_CTA_LABEL}
      </CtaButton>
      <StickyCta
        type="anchor"
        anchorId={anchorId}
        availableForSale={availableForSale}
        isSubscription={false}
      />
    </Flex>
  );
}
